import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CITIES, isAgent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { serviceLabel, serviceBadge } from "@/lib/api";
import { ReferralEditor } from "@/components/ReferralEditor";
import { Inbox, Coins, BadgeCheck, Clock, MapPin, Phone, Mail, Lock, TrendingUp, Building2, Trophy } from "lucide-react";

function ProfileSetup({ onCreated }) {
  const [f, setF] = useState({ name: "", category: "home_inspection", city: "Sacramento", zip: "", phone: "", website: "", description: "", services: "" });
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name || !f.zip) { toast.error("Business name and ZIP are required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, services: f.services.split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.post("/businesses", payload);
      toast.success("Profile created! Start your free trial to go live.");
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="container-x py-14 max-w-2xl">
      <div className="text-center mb-8">
        <Building2 className="w-10 h-10 text-[color:var(--emerald)] mx-auto mb-3" />
        <h1 className="font-display text-3xl font-extrabold text-[color:var(--navy)]">Set up your business profile</h1>
        <p className="text-[color:var(--ink-2)] mt-2">Get listed in the directory and start receiving Sacramento-area leads.</p>
      </div>
      <div className="card-soft p-8 space-y-4" data-testid="profile-setup-form">
        <div><Label>Business Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name-input" className="mt-1" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Category</Label>
            <select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="profile-category-select" className="w-full h-11 rounded-lg border border-slate-300 px-3 mt-1 bg-white">
              {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              <option value="both">Inspection & Pest</option>
            </select>
          </div>
          <div><Label>City</Label>
            <select value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="profile-city-select" className="w-full h-11 rounded-lg border border-slate-300 px-3 mt-1 bg-white">
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>ZIP</Label><Input value={f.zip} onChange={(e) => set("zip", e.target.value)} data-testid="profile-zip-input" className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone-input" className="mt-1" /></div>
        </div>
        <div><Label>Services (comma separated)</Label><Input value={f.services} onChange={(e) => set("services", e.target.value)} placeholder="Home Inspection, Roof, Termite" data-testid="profile-services-input" className="mt-1" /></div>
        <div><Label>Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="profile-website-input" className="mt-1" /></div>
        <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="profile-description-input" className="mt-1" /></div>
        <Button disabled={saving} onClick={submit} data-testid="profile-submit-button" className="btn-primary rounded-full w-full font-semibold">{saving ? "Creating..." : "Create Profile"}</Button>
      </div>
    </div>
  );
}

export default function MemberDashboard() {
  const { user, business, loading, setBusiness, refresh } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("new");

  useEffect(() => { if (!loading && !user) nav("/"); }, [loading, user, nav]);
  const loadLeads = () => api.get("/leads/my").then(({ data }) => setLeads(data)).catch(() => {});
  useEffect(() => { if (business) loadLeads(); }, [business]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-[color:var(--ink-3)]">Loading...</div>;
  if (!user) return null;
  if (!business) return <ProfileSetup onCreated={(b) => setBusiness(b)} />;

  const claim = async (id) => {
    try {
      await api.post(`/leads/${id}/claim`);
      toast.success("Lead claimed! Contact details unlocked.");
      await refresh(); loadLeads();
    } catch (e) {
      if (e.response?.status === 402) { toast.error("Out of credits. Redirecting to pricing..."); setTimeout(() => nav("/pricing"), 1200); }
      else toast.error(e.response?.data?.detail || "Failed to claim");
    }
  };

  const setPipeline = async (id, status) => {
    try {
      await api.put(`/leads/${id}/pipeline`, { status });
      toast.success(`Marked ${status}`);
      loadLeads();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const newLeads = leads.filter((l) => !l.claimed);
  const claimed = leads.filter((l) => l.claimed);
  const shown = tab === "new" ? newLeads : claimed;

  const agent = isAgent(business.category);
  const allStats = [
    { label: "Lead Credits", value: business.credits, icon: Coins, color: "var(--amber)", testid: "member-credit-balance-badge" },
    { label: "New Leads", value: newLeads.length, icon: Inbox, color: "var(--emerald)" },
    { label: "Claimed", value: claimed.length, icon: BadgeCheck, color: "var(--navy)" },
    { label: "Deals Won", value: claimed.filter((l) => l.pipeline === "won").length, icon: Trophy, color: "var(--emerald)", testid: "member-deals-won" },
    { label: "Plan", value: business.subscription_tier ? business.subscription_tier.replace("_", " ") : "None", icon: TrendingUp, color: "var(--emerald)", testid: "member-plan-stat" },
    { label: "Reviews", value: business.review_count || 0, icon: BadgeCheck, color: "var(--navy)", agentOnly: true },
  ];
  const stats = allStats.filter((s) => (agent ? ["Plan", "Reviews"].includes(s.label) : !s.agentOnly));

  return (
    <div className="container-x py-12" data-testid="member-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[color:var(--navy)]">{business.name}</h1>
          <div className="flex items-center gap-2 text-sm text-[color:var(--ink-3)] mt-1">
            <MapPin className="w-4 h-4" /> {business.city}, CA
            {business.approved ? <span className="badge-verified px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Approved</span>
              : <span className="badge-pest px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Not active · start trial</span>}
          </div>
        </div>
        <Button onClick={() => nav("/pricing")} className="btn-amber rounded-full font-semibold" data-testid="member-buy-credits-button">{isAgent(business.category) ? "Manage Plan" : "Buy Credits / Upgrade"}</Button>
      </div>

      <div className={`grid grid-cols-2 gap-4 mb-8 ${agent ? "lg:grid-cols-2" : "lg:grid-cols-5"}`}>
        {stats.map((s) => (
          <div key={s.label} className="card-soft p-5" data-testid={s.testid}>
            <s.icon className="w-6 h-6 mb-2" style={{ color: s.color }} />
            <div className="font-display text-3xl font-extrabold text-[color:var(--navy)] capitalize">{s.value}</div>
            <div className="text-sm text-[color:var(--ink-3)]">{s.label}</div>
          </div>
        ))}
      </div>

      {!business.approved && (
        <div className="card-soft p-5 mb-6 !bg-amber-50 !border-amber-200 text-amber-800 text-sm" data-testid="member-pending-notice">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <span>Almost there! Start your 60-day free trial to go live in the directory and begin receiving leads.</span>
            <Button onClick={() => nav("/pricing")} size="sm" className="btn-amber rounded-full" data-testid="member-activate-button">Start free trial</Button>
          </div>
        </div>
      )}

      {business.subscription_status === "trialing" && business.trial_ends_at && (
        <div className="card-soft p-5 mb-6 !bg-emerald-50 !border-emerald-200 text-emerald-800 text-sm" data-testid="member-trial-notice">
          Free trial active until {new Date(business.trial_ends_at).toLocaleDateString()}. Your plan continues automatically after that.
        </div>
      )}
      {["past_due", "canceled"].includes(business.subscription_status) && (
        <div className="card-soft p-5 mb-6 !bg-red-50 !border-red-200 text-red-800 text-sm flex items-center justify-between gap-4" data-testid="member-billing-notice">
          <span>{business.subscription_status === "past_due" ? "Your last payment failed. Please update your card to keep your membership." : "Your membership has been canceled."}</span>
          <Button onClick={() => nav("/pricing")} size="sm" className="btn-amber rounded-full" data-testid="member-billing-action">{business.subscription_status === "past_due" ? "Fix billing" : "Rejoin"}</Button>
        </div>
      )}

      {isAgent(business.category) ? (
        <div className="card-soft p-8 text-center" data-testid="agent-partner-notice">
          <h3 className="font-display text-xl font-bold text-[color:var(--navy)]">Agent Partner membership</h3>
          <p className="text-sm text-[color:var(--ink-2)] mt-2 max-w-lg mx-auto">Your profile is listed in the Agents section of our directory so local homeowners can find you. Agent Partner plans don't include homeowner leads.</p>
          <Button onClick={() => nav("/directory?cat=agents")} variant="outline" className="rounded-full mt-5" data-testid="agent-view-listing-button">View Agents Directory</Button>
        </div>
      ) : null}
      {isAgent(business.category) ? (
        <ReferralEditor business={business} />
      ) : (<>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("new")} data-testid="member-tab-new" className={`px-5 py-2 rounded-full text-sm font-semibold ${tab === "new" ? "btn-primary" : "bg-[color:var(--bg-3)] text-[color:var(--ink-2)]"}`}>New Leads ({newLeads.length})</button>
        <button onClick={() => setTab("claimed")} data-testid="member-tab-claimed" className={`px-5 py-2 rounded-full text-sm font-semibold ${tab === "claimed" ? "btn-primary" : "bg-[color:var(--bg-3)] text-[color:var(--ink-2)]"}`}>My Claimed ({claimed.length})</button>
      </div>

      {shown.length === 0 ? (
        <div className="card-soft p-12 text-center text-[color:var(--ink-3)]" data-testid="member-leads-empty">No {tab === "new" ? "new" : "claimed"} leads yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {shown.map((l) => (
            <div key={l.id} className="card-soft p-6" data-testid={`member-lead-${l.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${serviceBadge(l.service_type)}`}>{serviceLabel(l.service_type)}</span>
                    <span className="text-xs badge-verified px-2 py-0.5 rounded-full uppercase">{l.urgency}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg text-[color:var(--navy)] mt-2">{l.name}</h3>
                  <div className="text-sm text-[color:var(--ink-3)] flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {l.city}, {l.zip}</div>
                </div>
              </div>
              <p className="text-sm text-[color:var(--ink-2)] mt-3">{l.details || "No extra details provided."}</p>
              {l.claimed ? (
                <>
                  <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100 space-y-1.5" data-testid={`member-lead-contact-${l.id}`}>
                    <div className="flex items-center gap-2 text-sm text-[color:var(--navy)]"><Phone className="w-4 h-4 text-[color:var(--emerald)]" /> {l.phone}</div>
                    <div className="flex items-center gap-2 text-sm text-[color:var(--navy)]"><Mail className="w-4 h-4 text-[color:var(--emerald)]" /> {l.email}</div>
                    {l.address && <div className="flex items-center gap-2 text-sm text-[color:var(--navy)]"><MapPin className="w-4 h-4 text-[color:var(--emerald)]" /> {l.address}</div>}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`member-lead-pipeline-${l.id}`}>
                    <span className="text-xs text-[color:var(--ink-3)] font-medium">Status:</span>
                    {["contacted", "won", "lost"].map((s) => (
                      <button key={s} onClick={() => setPipeline(l.id, s)} data-testid={`member-lead-status-${s}-${l.id}`}
                        className={`text-xs font-semibold px-3 py-1 rounded-full capitalize transition-colors ${(l.pipeline || "new") === s ? (s === "won" ? "badge-verified" : s === "lost" ? "badge-pest" : "badge-ins") : "bg-[color:var(--bg-3)] text-[color:var(--ink-2)] hover:bg-slate-200"}`}>{s}</button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="mt-4 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm text-[color:var(--ink-3)]"><Lock className="w-4 h-4" /> Contact locked</span>
                  <Button onClick={() => claim(l.id)} data-testid={`member-lead-claim-button-${l.id}`} className="btn-amber rounded-full font-semibold" disabled={!business.approved}>Claim (1 credit)</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
